const _extend = Object.assign;
const sb = require("./sphericalbounds");
const geometry = require("../maps/geometry");

/**
 * MapZoomAndCenter
 * @typedef {Object} MapZoomAndCenter
 * @private
 * @property {google.maps.LatLng} center the center coordinates.
 * @property {Number} zoom the zoom.
 */

/**
 * BoundsPadding
 * All properties values are expected to be in pixels.
 * @typedef {Object} BoundsPadding
 * @property {Number} [top]
 * @property {Number} [bottom]
 * @property {Number} [right]
 * @property {Number} [left]
 */

/**
 * @typedef {Object} BoundsOptions
 * @private
 * @property {BoundsPadding} [padding]
 * @property {Number} [maxZoom]
 * @property {Number} mapHeight
 * @property {Number} mapWidth
 */

class BoundsManager {
    /**
     * Computes the zoom and the center of the map for fitting bounds
     * @param {BoundsOptions} options
     * @private
     * @constructor
     */
    constructor(options) {
        this.options = _extend(
            {
                padding: {},
                maxZoom: 21,
                mapHeight: 100,
                mapWidth: 100,
            },
            options,
        );

        this.padding = _extend(
            {
                top: 20,
                bottom: 20,
                left: 20,
                right: 20,
            },
            this.options.padding,
        );
    }

    /**
     * Computes the center of the map to display the data.
     * Takes in account the padding.
     * @param {google.maps.LatLngBounds|woosmap.maps.geometry.LatLngBounds} bounds
     * @param {Number} zoom
     * @returns {google.maps.LatLng} the computed center
     */
    getCenterWithPadding(bounds, zoom) {
        const newBounds = this.getPaddedBounds(bounds, zoom, this.padding);
        return newBounds.getCenter();
    }

    /**
     * Computes pixel padded bounds.
     * @param {google.maps.LatLngBounds|woosmap.maps.geometry.LatLngBounds} bounds
     * @param {Number} zoom
     * @param {BoundsPadding} padding
     * @returns {google.maps.LatLngBounds}
     */
    getPaddedBounds(bounds, zoom, padding) {
        const meterBounds = sb.fromLatLngBounds(bounds);

        const pixelBounds = meterBounds.toPixel(zoom);

        pixelBounds.sw.x -= padding.left;
        pixelBounds.ne.x += padding.right;

        pixelBounds.sw.y -= padding.bottom;
        pixelBounds.ne.y += padding.top;

        return pixelBounds.toLatLngBounds(zoom);
    }

    /**
     * Computes a padded center to allow panTo
     * @param {google.maps.LatLng} point
     * @param {Number} zoom
     */
    getPaddedCenter(point, zoom) {
        const mCenter = sb.converters.fromLatLng(point);
        const res = sb.initialResolution / 2 ** zoom;

        const mWidth = this.options.mapWidth * res;
        const mHeight = this.options.mapHeight * res;

        const x1 = mCenter.x - mWidth / 2.0;
        const y1 = mCenter.y - mHeight / 2.0;

        const x2 = mCenter.x + mWidth / 2.0;
        const y2 = mCenter.y + mHeight / 2.0;

        const bounds = new geometry.LatLngBounds(
            sb.converters.toLatLng({
                x: x1,
                y: y1,
            }),
            sb.converters.toLatLng({
                x: x2,
                y: y2,
            }),
        );

        return this.getCenterWithPadding(bounds, zoom);
    }

    /**
     * Computes the bounds of the visible bounds (ie. the padding is substracted from the bounds)
     * @param {google.maps.LatLngBounds} bounds
     * @param {Number} zoom
     */
    getVisibleBounds(bounds, zoom) {
        const invertedPadding = {};

        for (const p in this.padding) {
            if (this.padding.hasOwnProperty(p)) {
                invertedPadding[p] = -this.padding[p];
            }
        }

        return this.getPaddedBounds(bounds, zoom, invertedPadding);
    }

    /**
     * Computes `zoom` for the `bounds` passed as parameter.
     * Takes in account the padding.
     * @param {google.maps.LatLngBounds|woosmap.maps.geometry.LatLngBounds} bounds
     * @returns {Number}
     */
    getZoomForBounds(bounds) {
        const worldSize = {
            height: 256,
            width: 256,
        };
        const mapSize = {
            width: this.options.mapWidth - (this.padding.left + this.padding.right),
            height: this.options.mapHeight - (this.padding.top + this.padding.bottom),
        };

        function latRad(lat) {
            const sin = Math.sin((lat * Math.PI) / 180);
            const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
            return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
        }

        function zoom(mapPx, worldPx, fraction) {
            return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
        }

        const ZOOM_MAX = this.options.maxZoom;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        const latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;

        const lngDiff = ne.lng - sw.lng;
        const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

        let latZoom = ZOOM_MAX;
        let lngZoom = ZOOM_MAX;

        if (lngFraction > 0) {
            lngZoom = zoom(mapSize.width, worldSize.width, lngFraction);
        }

        if (latFraction > 0) {
            latZoom = zoom(mapSize.height, worldSize.height, latFraction);
        }

        return Math.min(latZoom, lngZoom, ZOOM_MAX);
    }

    /**
     * Computes a `zoom` and a `center` for bounds with padding.
     * @param {google.maps.LatLngBounds|BMap.Bounds} bounds
     * @returns {MapZoomAndCenter} the center and zoom.
     */
    getBoundsCenterAndZoom(bounds) {
        const _bounds = new geometry.LatLngBounds(bounds.getNorthEast(), bounds.getSouthWest());

        const zoom = this.getZoomForBounds(_bounds);
        const center = this.getCenterWithPadding(_bounds, zoom);

        return {
            center: center,
            zoom: zoom,
        };
    }
}

exports.BoundsManager = BoundsManager;
